import React, {useState, useEffect, useCallback} from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import './ModalWide.css'

const extractTreatedSymptomId = (treatedSymptom) => (
    treatedSymptom?.treated_symptom_id
    ?? treatedSymptom?.treated_symptom?.treated_symptom_id
    ?? treatedSymptom?.treated_symptom?.id
    ?? treatedSymptom?.id
    ?? treatedSymptom?.treated_symptom
    ?? null
);

const extractProtocolTreatedSymptomId = (treatedSymptom) => (
    treatedSymptom?.protocol_treated_symptom_id
    ?? treatedSymptom?.protocol_treated_symptom?.protocol_treated_symptom_id
    ?? treatedSymptom?.protocol_treated_symptom?.id
    ?? treatedSymptom?.protocol_treated_symptoms?.[0]?.protocol_treated_symptom_id
    ?? treatedSymptom?.protocol_treated_symptoms?.[0]?.id
    ?? null
);

const extractTreatedSymptomLabel = (treatedSymptom) => (
    treatedSymptom?.symptom
    ?? treatedSymptom?.treated_symptom?.symptom
    ?? treatedSymptom?.description
    ?? ''
);

const normalizeTreatedSymptoms = (treatedSymptoms = []) => {
    const uniqueTreatedSymptoms = new Map();

    (treatedSymptoms ?? []).forEach((treatedSymptom) => {
        if (treatedSymptom === null || treatedSymptom === undefined) {
            return;
        }

        const normalizedTreatedSymptom = typeof treatedSymptom === 'string'
            ? { id: treatedSymptom, symptom: treatedSymptom, protocol_treated_symptom_id: null }
            : {
                id: extractTreatedSymptomId(treatedSymptom),
                symptom: extractTreatedSymptomLabel(treatedSymptom),
                protocol_treated_symptom_id: extractProtocolTreatedSymptomId(treatedSymptom)
            };

        const key = String(normalizedTreatedSymptom.id ?? normalizedTreatedSymptom.symptom).trim().toLowerCase();
        if (!key || !normalizedTreatedSymptom.symptom) {
            return;
        }

        uniqueTreatedSymptoms.set(key, normalizedTreatedSymptom);
    });

    return Array.from(uniqueTreatedSymptoms.values());
};

const mergeTreatedSymptomOptions = (options = [], selected = []) => {
    const mergedOptions = new Map();

    [...options, ...selected].forEach((treatedSymptom) => {
        const key = String(treatedSymptom.id ?? treatedSymptom.symptom).trim().toLowerCase();
        if (!key) {
            return;
        }

        mergedOptions.set(key, treatedSymptom);
    });

    return Array.from(mergedOptions.values());
};

const areTreatedSymptomsEqual = (left = [], right = []) => {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((treatedSymptom, index) => {
        const compareTreatedSymptom = right[index];
        return String(treatedSymptom?.id ?? treatedSymptom?.symptom ?? '') === String(compareTreatedSymptom?.id ?? compareTreatedSymptom?.symptom ?? '')
            && String(treatedSymptom?.symptom ?? '').trim().toLowerCase() === String(compareTreatedSymptom?.symptom ?? '').trim().toLowerCase()
            && String(treatedSymptom?.protocol_treated_symptom_id ?? '') === String(compareTreatedSymptom?.protocol_treated_symptom_id ?? '');
    });
};

const findSelectedOption = (options, id, description) => {
    const optionById = options.find((option) => String(option.id) === String(id));
    if (optionById) {
        return optionById;
    }

    const normalizedDescription = String(description ?? id ?? '').trim().toLowerCase();
    return options.find((option) => String(option.description ?? '').trim().toLowerCase() === normalizedDescription);
};

const ModalProtocolEdit = ({closeModal, onSubmit, protocolEdit, isNewProtocol, allProtocolTypes, allMusicTypes, allTreatedSymptoms, isTreatedSymptomsLoading = false, allProtocols = [], isReadOnly = false, onOpenSessionHistory, onOpenProtocolLightData, onOpenNotes}) => {
    const isReadOnlyMode = !!isReadOnly;
    const protocolTypes = allProtocolTypes ?? [];
    const musicTypes = allMusicTypes ?? [];
    const treatedSymptomLookupOptions = normalizeTreatedSymptoms(allTreatedSymptoms ?? []);
    const protocolOptions = (allProtocols ?? []).filter((protocol) => protocol?.id !== undefined && protocol?.id !== null);

    const buildNewProtocol = () => {
        const newProtocolData = [];
        for (let i = 1; i < 8; i++) {
            const addLightGroup = {
                light: 'green ir1060 ir810 ir850 ir940 red660',
                frequency: '',
                duty_cycle: '50'
            };
            const addSequence = {
                duration: 3,
                sequence: i,
                light_groups: [addLightGroup]
            };
            newProtocolData.push(addSequence);
        }

        return {
            description: '',
            detailed_description: '',
            treated_symptoms: [],
            protocol_type: 2,
            protocol_type_description: 'American',
            is_authorized: true,
            is_active: true,
            name: '',
            music_type: 0,
            music_type_description: 'Any',
            protocol_data: newProtocolData,
        };
    };

    const initialProtocol = isNewProtocol ? buildNewProtocol() : {
        ...(protocolEdit ?? {}),
        treated_symptoms: normalizeTreatedSymptoms(protocolEdit?.treated_symptoms ?? []),
        music_type_description: protocolEdit?.music_type_description ?? protocolEdit?.music_type ?? ''
    };

    const [protocolInfo, setProtocolInfo] = useState(initialProtocol);
    const [errors, setErrors] = useState('');
    const [copySourceProtocolId, setCopySourceProtocolId] = useState('none');
    const [copiedProtocolData, setCopiedProtocolData] = useState(null);
    const [copyDataError, setCopyDataError] = useState('');
    const [isCopyDataLoading, setIsCopyDataLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [createdProtocolId, setCreatedProtocolId] = useState(initialProtocol.id ?? null);
    const [isProtocolDetailsLoading, setIsProtocolDetailsLoading] = useState(!isNewProtocol && !!(protocolEdit?.id ?? initialProtocol.id));
    const [removedTreatedSymptoms, setRemovedTreatedSymptoms] = useState([]);

    const treatedSymptomOptions = mergeTreatedSymptomOptions(treatedSymptomLookupOptions, protocolInfo.treated_symptoms ?? []);
    const isTreatedSymptomsPending = isTreatedSymptomsLoading || isProtocolDetailsLoading;

    useEffect(() => {
        if ((allProtocolTypes ?? []).length > 0) {
            setProtocolInfo((prev) => {
                const selectedType = findSelectedOption(
                    allProtocolTypes,
                    prev.protocol_type_id ?? prev.protocol_type,
                    prev.protocol_type_description
                );

                if (!selectedType
                    || (String(prev.protocol_type_id ?? '') === String(selectedType.id)
                        && String(prev.protocol_type ?? '') === String(selectedType.id)
                        && prev.protocol_type_description === selectedType.description)) {
                    return prev;
                }

                return {
                    ...prev,
                    protocol_type_description: selectedType.description,
                    protocol_type: selectedType.id,
                    protocol_type_id: selectedType.id
                };
            });
        }

        if ((allMusicTypes ?? []).length > 0 && !protocolInfo.music_type_description) {
            const firstMusic = allMusicTypes[0];
            setProtocolInfo((prev) => ({
                ...prev,
                music_type_description: firstMusic.description,
                music_type: firstMusic.id,
                music_type_id: firstMusic.id
            }));
        }
    }, [allProtocolTypes, allMusicTypes, protocolInfo.protocol_type_description, protocolInfo.music_type_description]);

    useEffect(() => {
        if (!treatedSymptomLookupOptions.length || !(protocolInfo.treated_symptoms ?? []).length) {
            return;
        }

        setProtocolInfo((prev) => {
            const normalizedTreatedSymptoms = normalizeTreatedSymptoms((prev.treated_symptoms ?? []).map((treatedSymptom) => {
                const matchedTreatedSymptom = treatedSymptomLookupOptions.find((option) => (
                    String(option.id) === String(treatedSymptom.id)
                    || option.symptom.trim().toLowerCase() === String(treatedSymptom.symptom ?? '').trim().toLowerCase()
                ));

                if (!matchedTreatedSymptom) {
                    return treatedSymptom;
                }

                return {
                    ...matchedTreatedSymptom,
                    protocol_treated_symptom_id: treatedSymptom.protocol_treated_symptom_id ?? matchedTreatedSymptom.protocol_treated_symptom_id ?? null
                };
            }));

            if (areTreatedSymptomsEqual(prev.treated_symptoms ?? [], normalizedTreatedSymptoms)) {
                return prev;
            }

            return {
                ...prev,
                treated_symptoms: normalizedTreatedSymptoms
            };
        });
    }, [protocolInfo.treated_symptoms, treatedSymptomLookupOptions]);

    const fetchProtocolDetails = useCallback(async (protocolId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocols/protocol/' + protocolId,
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            });

        if (!response.ok) {
            throw new Error(await readErrorMessage(response));
        }

        const json = await response.json();
        return json?.[0] ?? null;
    }, []);

    useEffect(() => {
        if (isNewProtocol || !(protocolEdit?.id || protocolInfo.id)) {
            setIsProtocolDetailsLoading(false);
            return;
        }

        let isCancelled = false;
        setIsProtocolDetailsLoading(true);

        const hydrateProtocolDetails = async () => {
            try {
                const hydratedProtocol = await fetchProtocolDetails(protocolEdit?.id ?? protocolInfo.id);
                if (!hydratedProtocol || isCancelled) {
                    return;
                }

                setProtocolInfo((prev) => ({
                    ...prev,
                    detailed_description: hydratedProtocol.detailed_description ?? prev.detailed_description ?? '',
                    treated_symptoms: normalizeTreatedSymptoms(hydratedProtocol.treated_symptoms ?? prev.treated_symptoms ?? [])
                }));
                setRemovedTreatedSymptoms([]);
            } catch (error) {
                console.error(error);
            } finally {
                if (!isCancelled) {
                    setIsProtocolDetailsLoading(false);
                }
            }
        };

        hydrateProtocolDetails();

        return () => {
            isCancelled = true;
        };
    }, [fetchProtocolDetails, isNewProtocol, protocolEdit?.id, protocolInfo.id]);

    const readErrorMessage = async (response) => {
        try {
            const text = await response.text();
            if (!text.trim()) {
                return `Fetch error - ${response.url} - Status: ${response.status}`;
            }

            let payload = null;
            try {
                payload = JSON.parse(text);
            } catch {
                return text.trim();
            }

            if (typeof payload === 'string' && payload.trim()) {
                return payload.trim();
            }

            if (payload && typeof payload === 'object') {
                const flattenedMessage = Object.entries(payload)
                    .flatMap(([key, value]) => {
                        if (Array.isArray(value)) {
                            return value.map((item) => `${key}: ${item}`);
                        }

                        if (value && typeof value === 'object') {
                            return `${key}: ${JSON.stringify(value)}`;
                        }

                        return value ? `${key}: ${value}` : [];
                    })
                    .filter(Boolean)
                    .join(' ');

                if (flattenedMessage) {
                    return flattenedMessage;
                }
            }
        } catch {
            return `Fetch error - ${response.url} - Status: ${response.status}`;
        }

        return `Fetch error - ${response.url} - Status: ${response.status}`;
    };

    const extractProtocolId = (payload) => (
        payload?.protocol_id
        ?? payload?.id
        ?? payload?.[0]?.protocol_id
        ?? payload?.[0]?.id
        ?? null
    );

    const apiPatchProtocolInfo = async (protocolId, patchProtocolInfo) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocols/protocol/' + protocolId,
            {
                method: 'PATCH',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchProtocolInfo),
            });
        if (!response.ok) {
            throw new Error(await readErrorMessage(response));
        }
        await response.json();
    }

    const apiPostNewProtocol = async (patchProtocolInfo) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocols/',
            {
                method: 'POST',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify(patchProtocolInfo),
            });
        if (!response.ok) {
            throw new Error(await readErrorMessage(response));
        }

        const payload = await response.json();
        console.log('Protocol Added Successfully')
        return payload;
    }

    const fetchProtocolSequenceData = async (protocolId) => {
        const protocolDetails = await fetchProtocolDetails(protocolId);
        return protocolDetails?.protocol_data ?? [];
    }

    const addProtocolTreatedSymptom = async (protocolId, treatedSymptomId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_treated_symptoms/',
            {
                method: 'POST',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: JSON.stringify({
                    protocol_id: Number(protocolId),
                    treated_symptom_id: Number(treatedSymptomId)
                }),
            });

        if (!response.ok) {
            throw new Error(await readErrorMessage(response));
        }
    }

    const removeProtocolTreatedSymptom = async (protocolTreatedSymptomId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_treated_symptoms/' + protocolTreatedSymptomId + '/',
            {
                method: 'DELETE',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    'content-type': 'application/json'
                },
                body: '',
            });

        if (!response.ok) {
            throw new Error(await readErrorMessage(response));
        }
    }

    const fetchProtocolTreatedSymptomLinks = async (protocolId) => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_treated_symptoms/',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            });

        if (!response.ok) {
            throw new Error(await readErrorMessage(response));
        }

        const json = await response.json();
        return (Array.isArray(json) ? json : [])
            .filter((item) => Number(item?.protocol_id ?? item?.protocol?.protocol_id ?? item?.protocol) === Number(protocolId))
            .map((item) => ({
                id: extractTreatedSymptomId(item),
                symptom: extractTreatedSymptomLabel(item),
                protocol_treated_symptom_id: extractProtocolTreatedSymptomId(item)
            }))
            .filter((item) => item.id !== null && item.id !== undefined);
    }

    const syncProtocolTreatedSymptoms = async (protocolId, selectedTreatedSymptoms, removedSelections = []) => {
        const hydratedProtocol = await fetchProtocolDetails(protocolId);
        const currentTreatedSymptoms = normalizeTreatedSymptoms(hydratedProtocol?.treated_symptoms ?? []);
        const nextTreatedSymptoms = normalizeTreatedSymptoms(selectedTreatedSymptoms ?? []);

        const currentIds = new Set(currentTreatedSymptoms.map((treatedSymptom) => String(treatedSymptom.id)));
        const nextIds = new Set(nextTreatedSymptoms.map((treatedSymptom) => String(treatedSymptom.id)));
        const removedSelectionsById = new Map(
            normalizeTreatedSymptoms(removedSelections).map((treatedSymptom) => [String(treatedSymptom.id), treatedSymptom])
        );

        let fallbackLinksById = null;
        const getFallbackLinksById = async () => {
            if (fallbackLinksById) {
                return fallbackLinksById;
            }

            const fallbackLinks = await fetchProtocolTreatedSymptomLinks(protocolId);
            fallbackLinksById = new Map(fallbackLinks.map((treatedSymptom) => [String(treatedSymptom.id), treatedSymptom]));
            return fallbackLinksById;
        };

        for (const treatedSymptom of currentTreatedSymptoms) {
            if (treatedSymptom.id !== undefined && treatedSymptom.id !== null && !nextIds.has(String(treatedSymptom.id))) {
                let protocolTreatedSymptomId = treatedSymptom.protocol_treated_symptom_id;

                if (protocolTreatedSymptomId === undefined || protocolTreatedSymptomId === null) {
                    protocolTreatedSymptomId = removedSelectionsById.get(String(treatedSymptom.id))?.protocol_treated_symptom_id ?? null;
                }

                if (protocolTreatedSymptomId === undefined || protocolTreatedSymptomId === null) {
                    protocolTreatedSymptomId = (await getFallbackLinksById()).get(String(treatedSymptom.id))?.protocol_treated_symptom_id ?? null;
                }

                if (protocolTreatedSymptomId === undefined || protocolTreatedSymptomId === null) {
                    throw new Error(`Missing protocol_treated_symptom_id for treated symptom ${treatedSymptom.symptom ?? treatedSymptom.id}.`);
                }

                await removeProtocolTreatedSymptom(protocolTreatedSymptomId);
            }
        }

        for (const treatedSymptom of nextTreatedSymptoms) {
            if (treatedSymptom.id !== undefined && treatedSymptom.id !== null && !currentIds.has(String(treatedSymptom.id))) {
                await addProtocolTreatedSymptom(protocolId, treatedSymptom.id);
            }
        }
    }

    const validateForm = async () => {
        setSubmitError('');
        const patchProtocolInfo = {...protocolInfo}
        if (patchProtocolInfo.name && patchProtocolInfo.protocol_type_description ){
            setErrors('')
            try {
                const protocolId = patchProtocolInfo.id
                const keysToRemove = [
                    'id',
                    'created_on',
                    'last_updated',
                    'treated_symptoms',
                    'music_type_description',
                    'protocol_type_description'
                ]
                keysToRemove.forEach((e) => delete patchProtocolInfo[e])
                if (isNewProtocol) {
                    // API requires a non-empty description for create, so default it when omitted.
                    if (!String(patchProtocolInfo.description ?? '').trim()) {
                        patchProtocolInfo.description = String(patchProtocolInfo.name ?? 'New Protocol').trim() || 'New Protocol';
                    }

                    if (copySourceProtocolId !== 'none') {
                        try {
                            let protocolDataToCopy = copiedProtocolData;
                            if (!protocolDataToCopy) {
                                protocolDataToCopy = await fetchProtocolSequenceData(copySourceProtocolId);
                            }

                            if (Array.isArray(protocolDataToCopy)) {
                                patchProtocolInfo.protocol_data = protocolDataToCopy.map((sequence) => ({
                                    ...sequence,
                                    light_groups: Array.isArray(sequence.light_groups)
                                        ? sequence.light_groups.map((lightGroup) => ({ ...lightGroup }))
                                        : [],
                                }));
                            }
                        } catch (error) {
                            console.error(error);
                            setCopyDataError('Unable to load protocol sequence data to copy.');
                            return false;
                        }
                    }

                    const protocolPayload = createdProtocolId
                        ? { protocol_id: createdProtocolId }
                        : await apiPostNewProtocol(patchProtocolInfo);
                    const savedProtocolId = createdProtocolId ?? extractProtocolId(protocolPayload);

                    if (!savedProtocolId) {
                        throw new Error('Protocol saved, but the new protocol ID was not returned.');
                    }

                    setCreatedProtocolId(savedProtocolId);
                    setProtocolInfo((prev) => ({ ...prev, id: savedProtocolId }));
                    await syncProtocolTreatedSymptoms(savedProtocolId, protocolInfo.treated_symptoms ?? [], removedTreatedSymptoms);
                }
                else {
                    const metadataPatch = {
                        name: String(patchProtocolInfo.name ?? '').trim(),
                        description: String(patchProtocolInfo.description ?? ''),
                        detailed_description: String(patchProtocolInfo.detailed_description ?? ''),
                        is_active: Boolean(patchProtocolInfo.is_active),
                        music_type: Number(patchProtocolInfo.music_type_id ?? patchProtocolInfo.music_type ?? 0),
                        protocol_type: Number(patchProtocolInfo.protocol_type_id ?? patchProtocolInfo.protocol_type ?? 0),
                    };

                    await apiPatchProtocolInfo(protocolId, metadataPatch)
                    await syncProtocolTreatedSymptoms(protocolId, protocolInfo.treated_symptoms ?? [], removedTreatedSymptoms);
                }
            } catch (error) {
                console.error(error);
                setSubmitError(error.message || 'Unable to save protocol changes.');
                return false;
            }

            return true;
        }
        else {
            const errorFields = [];
            for(const [key, value] of Object.entries(protocolInfo)) {
                if(!value){
                    errorFields.push(key);
                }
            }
            setErrors(errorFields.join(', '));
            return false;
        }
    }

    const handleChange = (e) => {
        setSubmitError('');
        setProtocolInfo({
            ...protocolInfo,
            [e.target.name]: e.target.value
        })
    }

    const handleTreatedSymptomsChange = (_, selectedTreatedSymptoms) => {
        setSubmitError('');
        const normalizedSelectedTreatedSymptoms = normalizeTreatedSymptoms(selectedTreatedSymptoms);
        const normalizedCurrentTreatedSymptoms = normalizeTreatedSymptoms(protocolInfo.treated_symptoms ?? []);
        const selectedIds = new Set(normalizedSelectedTreatedSymptoms.map((treatedSymptom) => String(treatedSymptom.id)));
        const newlyRemovedTreatedSymptoms = normalizedCurrentTreatedSymptoms.filter((treatedSymptom) => !selectedIds.has(String(treatedSymptom.id)));

        setRemovedTreatedSymptoms((previousRemovedTreatedSymptoms) => {
            const preservedRemovedTreatedSymptoms = normalizeTreatedSymptoms(previousRemovedTreatedSymptoms)
                .filter((treatedSymptom) => !selectedIds.has(String(treatedSymptom.id)));

            return normalizeTreatedSymptoms([...preservedRemovedTreatedSymptoms, ...newlyRemovedTreatedSymptoms]);
        });

        setProtocolInfo({
            ...protocolInfo,
            treated_symptoms: normalizedSelectedTreatedSymptoms
        })
    }

    const handleTypeChange = (e) => {
        setSubmitError('');
        const selectedTypeId = Number(e.target.value);
        const selectedType = protocolTypes.find((protocolType) => Number(protocolType.id) === selectedTypeId);
        setProtocolInfo({
            ...protocolInfo,
            protocol_type_description: selectedType?.description ?? '',
            protocol_type: selectedTypeId,
            protocol_type_id: selectedTypeId
        })
    }

     const handleMusicChange = (e) => {
          setSubmitError('');
        const selectedMusicId = Number(e.target.value);
        const selectedMusic = musicTypes.find((musicType) => Number(musicType.id) === selectedMusicId);
        setProtocolInfo({
            ...protocolInfo,
            music_type_description: selectedMusic?.description ?? '',
            music_type: selectedMusicId,
            music_type_id: selectedMusicId
        })
    }

    const handleCheckChange = (e) => {
        setSubmitError('');
        setProtocolInfo({
            ...protocolInfo,
            [e.target.name]: e.target.checked
        })
    }

    const handleCopySourceChange = async (e) => {
        const selectedProtocolId = e.target.value;
        setCopySourceProtocolId(selectedProtocolId);
        setCopiedProtocolData(null);
        setCopyDataError('');

        if (selectedProtocolId === 'none') {
            return;
        }

        setIsCopyDataLoading(true);
        try {
            const protocolData = await fetchProtocolSequenceData(selectedProtocolId);
            setCopiedProtocolData(protocolData);
        } catch (error) {
            console.error(error);
            setCopyDataError('Unable to load protocol sequence data to copy.');
        } finally {
            setIsCopyDataLoading(false);
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isCopyDataLoading || isSaving || isTreatedSymptomsPending) {
            return;
        }

        setIsSaving(true);
        let shouldClose = false;
        const isValidated = await validateForm()
        if (!isValidated) {
            setIsSaving(false);
            return;
        }

        onSubmit(protocolInfo);
        shouldClose = true;
        closeModal();

        if (!shouldClose) {
            setIsSaving(false);
        }
    }

    const handleCancel = (e) => {
        e.preventDefault();
        closeModal();
    }

    const handleOpenSessionHistory = (e) => {
        e.preventDefault();
        if (onOpenSessionHistory && protocolInfo?.id) {
            onOpenSessionHistory(protocolInfo);
        }
    }

    const handleOpenProtocolLightData = (e) => {
        e.preventDefault();
        if (onOpenProtocolLightData && protocolInfo?.id) {
            onOpenProtocolLightData(protocolInfo);
        }
    }

    const handleOpenNotes = (e) => {
        e.preventDefault();
        if (onOpenNotes && protocolInfo?.id) {
            onOpenNotes(protocolInfo);
        }
    }

    return (
    <div className='modal-container'>
      <div className='modal-edit'>
                <button className='modal-close-btn' onClick={handleCancel} aria-label='Close protocol details modal' disabled={isSaving}>X</button>
        <form>
            <div className='client-card-grid'>
                <section className='client-card'>
                    <div className='client-card-header'>
                        <h3>Protocol Details</h3>
                    </div>

                    <div className='client-fields-grid two-col'>
                        <div className='wide id-row'>
                            <label htmlFor='protocol_id' className='id-value'>ID: {isNewProtocol ? 'New Protocol' : (protocolInfo.id ?? '')}</label>
                            <div className='id-active-toggle'>
                                <input id='is_active' name='is_active' type='checkbox' value={protocolInfo.is_active} onChange={handleCheckChange} checked={!!protocolInfo.is_active} disabled={isReadOnlyMode}/>
                                <label htmlFor='is_active'>Active</label>
                            </div>
                        </div>

                        <div className='form-group'>
                            <label htmlFor='name'>Name*</label>
                            <input name='name' value={protocolInfo.name ?? ''} onChange={handleChange} readOnly={isReadOnlyMode}/>
                        </div>

                        <div className='form-group'>
                            <label htmlFor='protocol_type'>Type</label>
                            <select name='protocol_type_description' value={protocolInfo.protocol_type_id ?? protocolInfo.protocol_type ?? ''} onChange={handleTypeChange} disabled={isReadOnlyMode}>
                                {protocolTypes.map((protocol_type, index) => (
                                    <option key={protocol_type.id ?? index} value={protocol_type.id}>
                                        {protocol_type.description}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className='form-group wide'>
                            <label htmlFor='description'>Description</label>
                            <input
                                id='description'
                                name='description'
                                type='text'
                                maxLength={100}
                                value={protocolInfo.description ?? ''}
                                onChange={handleChange}
                                readOnly={isReadOnlyMode}
                            />
                        </div>

                        <div className='form-group wide'>
                            <label htmlFor='detailed_description'>Detailed Description</label>
                            <textarea
                                id='detailed_description'
                                name='detailed_description'
                                value={protocolInfo.detailed_description ?? ''}
                                onChange={handleChange}
                                rows={6}
                                readOnly={isReadOnlyMode}
                            />
                        </div>

                        <div className='form-group wide treated-symptoms-field'>
                            <label htmlFor='treated_symptoms'>Treated Symptoms</label>
                            {isTreatedSymptomsPending ? (
                                <div className='treated-symptoms-skeleton' aria-hidden='true'>
                                    <div className='skeleton-line treated-symptoms-skeleton-input' />
                                </div>
                            ) : (
                                <Autocomplete
                                    multiple
                                    id='treated_symptoms'
                                    options={treatedSymptomOptions}
                                    value={protocolInfo.treated_symptoms ?? []}
                                    onChange={handleTreatedSymptomsChange}
                                    getOptionLabel={(option) => option?.symptom ?? ''}
                                    isOptionEqualToValue={(option, value) => (
                                        String(option?.id ?? option?.symptom) === String(value?.id ?? value?.symptom)
                                    )}
                                    disableCloseOnSelect
                                    filterSelectedOptions
                                    readOnly={isReadOnlyMode}
                                    renderTags={(value, getTagProps) => value.map((option, index) => {
                                        const tagProps = getTagProps({ index });
                                        return (
                                            <Chip
                                                {...tagProps}
                                                key={String(option.id ?? option.symptom)}
                                                label={option.symptom}
                                                className='treated-symptom-chip'
                                                onDelete={isReadOnlyMode ? undefined : tagProps.onDelete}
                                            />
                                        );
                                    })}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={isReadOnlyMode ? '' : 'Search and add treated symptoms'}
                                        />
                                    )}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            alignItems: 'flex-start',
                                            minHeight: 56,
                                            borderRadius: '14px',
                                            backgroundColor: '#fbfdff',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d7dfeb',
                                        },
                                        '& .MuiAutocomplete-tag': {
                                            margin: '4px 6px 4px 0',
                                        },
                                        '& .MuiChip-root': {
                                            height: 30,
                                            borderRadius: '999px',
                                            background: 'linear-gradient(135deg, #dff3ea 0%, #c9e9da 100%)',
                                            border: '1px solid #a9d6c1',
                                            color: '#1c4d3a',
                                            fontWeight: 600,
                                        },
                                        '& .MuiAutocomplete-inputRoot': {
                                            paddingTop: '8px !important',
                                            paddingBottom: '8px !important',
                                        },
                                        '& .MuiAutocomplete-input': {
                                            minWidth: '240px !important',
                                        },
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: '#53738f',
                                        },
                                        '& .MuiAutocomplete-clearIndicator': {
                                            color: '#53738f',
                                        }
                                    }}
                                />
                            )}
                        </div>

                        <div className='form-group'>
                            <label htmlFor='music_type'>Music Type</label>
                            <select name='music_type' value={protocolInfo.music_type_id ?? protocolInfo.music_type ?? ''} onChange={handleMusicChange} disabled={isReadOnlyMode}>
                                {musicTypes.map((music_type, index) => (
                                    <option key={music_type.id ?? index} value={music_type.id}>
                                        {music_type.description}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {isNewProtocol && (
                            <div className='form-group wide'>
                                <label htmlFor='copy_sequence_from_protocol'>Copy Sequence Data From</label>
                                <select
                                    id='copy_sequence_from_protocol'
                                    name='copy_sequence_from_protocol'
                                    className='copy-sequence-select'
                                    value={copySourceProtocolId}
                                    onChange={handleCopySourceChange}
                                    disabled={isReadOnlyMode || isCopyDataLoading || isSaving}
                                >
                                    <option value='none'>None</option>
                                    {protocolOptions.map((protocol) => (
                                        <option key={protocol.id} value={protocol.id}>
                                            {protocol.name ? `${protocol.name} (ID: ${protocol.id})` : `Protocol ${protocol.id}`}
                                        </option>
                                    ))}
                                </select>
                                {isCopyDataLoading && <div className='legend'>Loading sequence data...</div>}
                                {!!copyDataError && <div className='error'>{copyDataError}</div>}
                            </div>
                        )}

                    </div>
                </section>
            </div>

            {!isNewProtocol && (
                <div className='protocol-modal-links-row'>
                    <button type='button' className='protocol-modal-link-btn' onClick={handleOpenSessionHistory}>Session History</button>
                    <button type='button' className='protocol-modal-link-btn' onClick={handleOpenProtocolLightData}>Protocol Light Data</button>
                    <button type='button' className='protocol-modal-link-btn' onClick={handleOpenNotes}>Add Notes</button>
                </div>
            )}

            {!isReadOnlyMode && errors && <div className='error'>{`Please include: ${errors}`}</div>}
            {!isReadOnlyMode && submitError && <div className='error'>{submitError}</div>}
            <div className='client-actions-row'>
                <button type='button' className='btn btn-cancel' onClick={handleCancel} disabled={isSaving}>{isReadOnlyMode ? 'Close' : 'Cancel'}</button>
                                {!isReadOnlyMode && <button type='submit' className='btn' onClick={handleSubmit} disabled={isCopyDataLoading || isSaving || isTreatedSymptomsPending}>{isSaving ? 'Saving...' : 'Save'}</button>}
            </div>
        </form>
      </div>

    </div>

  )

}

export default ModalProtocolEdit
